import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, Envelope, Lock, Sun, Moon } from "@phosphor-icons/react";

const LOGO_URL = "/bch-logo.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === "partner_store") {
        navigate("/partner-dashboard");
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      toast.success("Welcome back!", { description: `Logged in as ${userData.full_name}` });
      
      if (userData.role === "partner_store") {
        navigate("/partner-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error("Login Failed", { description: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-main flex">
      <SEO title="Sign In" description="Sign in to your BCH dashboard to manage referrals, attend meetings, and grow your business network." path="/login" />
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-card border border-[var(--border-color)] hover:bg-surface transition-colors"
        data-testid="login-theme-toggle"
      >
        {theme === "dark" ? (
          <Sun className="w-5 h-5 text-bch-gold" weight="duotone" />
        ) : (
          <Moon className="w-5 h-5 text-bch-teal" weight="duotone" />
        )}
      </button>
      
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('/login-bg.jpg')",
            filter: "brightness(0.3)"
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img src={LOGO_URL} alt="BCH Logo" className="h-28 w-auto object-contain" />
          <div>
            <h1 className="font-display text-5xl text-white mb-4">
              Connect. Collaborate. Grow.
            </h1>
            <p className="text-white/70 text-lg max-w-md">
              Join the premier business networking platform designed exclusively for verified business owners.
            </p>
          </div>
          <div className="flex gap-8">
            <div>
              <p className="font-mono text-3xl text-bch-gold font-bold">1000+</p>
              <p className="text-white/60 text-sm">Active Members</p>
            </div>
            <div>
              <p className="font-mono text-3xl text-bch-teal font-bold">₹50Cr+</p>
              <p className="text-white/60 text-sm">Business Exchanged</p>
            </div>
            <div>
              <p className="font-mono text-3xl text-bch-gold font-bold">25+</p>
              <p className="text-white/60 text-sm">Active Chapters</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src={LOGO_URL} alt="BCH Logo" className="h-20 w-auto object-contain" />
          </div>
          
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl text-primary mb-2">Welcome Back</h2>
            <p className="text-secondary">Sign in to access your BCH dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-secondary">Email Address</Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" weight="duotone" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="login-email-input"
                  className="pl-10 bg-surface border-[var(--border-color)] text-primary placeholder:text-muted focus:border-bch-teal focus:ring-1 focus:ring-bch-teal"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-secondary">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" weight="duotone" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                  className="pl-10 pr-10 bg-surface border-[var(--border-color)] text-primary placeholder:text-muted focus:border-bch-teal focus:ring-1 focus:ring-bch-teal"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-secondary"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" weight="duotone" /> : <Eye className="w-5 h-5" weight="duotone" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-bch-teal hover:bg-bch-teal-dark text-white font-medium py-3 rounded-sm transition-colors duration-300"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>

            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-bch-teal text-sm hover:text-bch-teal/80 transition-colors" data-testid="forgot-password-link">
                Forgot Password?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-secondary">
              Don't have an account?{" "}
              <Link to="/signup" className="text-bch-gold hover:text-bch-gold/80 font-medium transition-colors" data-testid="signup-link">
                Apply for membership
              </Link>
            </p>
            <p className="text-secondary mt-2">
              Want to become a partner store?{" "}
              <Link to="/partner-signup" className="text-bch-teal hover:text-bch-teal/80 font-medium transition-colors" data-testid="partner-signup-link">
                Apply here
              </Link>
            </p>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-bch-teal hover:text-bch-teal/80 text-sm transition-colors">
              ← Back to Home
            </Link>
          </div>

          <div className="mt-12 pt-8 border-t border-[var(--border-color)]">
            <p className="text-muted text-xs text-center">
              BCH is an exclusive network. Membership is by invitation and approval only.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
